<template>
  <transition name="pedido-drawer-fade">
    <div
      v-if="visible"
      class="pedido-drawer-layer"
      @mousedown.self="$emit('close')"
    >
      <aside
        ref="drawer"
        class="pedido-drawer"
        role="dialog"
        aria-modal="true"
        :aria-labelledby="titleId"
        tabindex="-1"
      >
        <header class="pedido-drawer-header">
          <div class="pedido-drawer-title">
            <span>Pedido online</span>
            <h2 :id="titleId">{{ code }}</h2>
            <div v-if="order" class="pedido-drawer-statusline">
              <span :class="statusClass">{{ statusLabel }}</span>
              <span>
                <i class="far fa-clock" aria-hidden="true"></i>
                {{ ageLabel }} nesta etapa
              </span>
            </div>
          </div>
          <button
            ref="closeButton"
            type="button"
            class="pedido-drawer-close"
            aria-label="Fechar detalhes do pedido"
            @click="$emit('close')"
          >
            <i class="fas fa-times" aria-hidden="true"></i>
          </button>
        </header>

        <div class="pedido-drawer-body" :aria-busy="loading ? 'true' : 'false'">
          <div v-if="loading && !order" class="drawer-loading" role="status">
            <span class="spinner-border" aria-hidden="true"></span>
            Carregando pedido...
          </div>

          <template v-else-if="order">
            <section
              class="drawer-highlight-grid"
              aria-label="Resumo do pedido"
            >
              <div>
                <span>Cliente</span>
                <strong>{{ customer }}</strong>
              </div>
              <div>
                <span>Modalidade</span>
                <strong>{{ deliveryLabel }}</strong>
              </div>
              <div>
                <span>Total</span>
                <strong>{{ money(order.total) }}</strong>
              </div>
              <div>
                <span>Pagamento</span>
                <strong>{{ paymentStatusLabel }}</strong>
              </div>
            </section>

            <section class="drawer-section">
              <div class="drawer-section-title">
                <h3>Itens do pedido</h3>
                <span>{{ itemCountLabel }}</span>
              </div>
              <div v-if="!items.length" class="drawer-empty">
                Nenhum item retornado pela API.
              </div>
              <article
                v-for="(item, index) in items"
                v-else
                :key="itemKey(item, index)"
                class="drawer-order-item"
              >
                <div class="drawer-item-main">
                  <span class="drawer-item-quantity">
                    {{ item.quantidade || 1 }}x
                  </span>
                  <div>
                    <strong>{{ itemName(item) }}</strong>
                    <ul v-if="itemDetails(item).length">
                      <li
                        v-for="(detail, detailIndex) in itemDetails(item)"
                        :key="`${index}-${detailIndex}`"
                      >
                        {{ detail }}
                      </li>
                    </ul>
                  </div>
                  <strong class="drawer-item-total">
                    {{ money(itemTotalValue(item)) }}
                  </strong>
                </div>
                <div
                  v-for="(child, childIndex) in itemChildren(item)"
                  :key="`child-${childIndex}`"
                  class="drawer-child-item"
                >
                  <i class="fas fa-level-up-alt" aria-hidden="true"></i>
                  <span>
                    {{ child.quantidade || 1 }}x {{ itemName(child) }}
                    <small v-if="child.setor || child.destino">
                      {{ child.setor || child.destino }}
                    </small>
                  </span>
                  <strong>{{ money(itemTotalValue(child)) }}</strong>
                </div>
              </article>
            </section>

            <section class="drawer-section">
              <h3>Pagamento</h3>
              <dl class="drawer-definition-list">
                <div>
                  <dt>Forma</dt>
                  <dd>{{ paymentMethod }}</dd>
                </div>
                <div>
                  <dt>Situação</dt>
                  <dd>{{ paymentStatusLabel }}</dd>
                </div>
                <div v-if="payment.troco_para">
                  <dt>Troco para</dt>
                  <dd>{{ money(payment.troco_para) }}</dd>
                </div>
              </dl>
            </section>

            <section class="drawer-section">
              <h3>{{ deliveryLabel }}</h3>
              <dl class="drawer-definition-list">
                <div v-if="phone">
                  <dt>Telefone</dt>
                  <dd>
                    <a :href="phoneHref">{{ phone }}</a>
                  </dd>
                </div>
                <div v-if="order.tipo_entrega === 'delivery'">
                  <dt>Endereço</dt>
                  <dd>{{ address }}</dd>
                </div>
                <div v-if="order.observacao">
                  <dt>Observação do pedido</dt>
                  <dd>{{ order.observacao }}</dd>
                </div>
                <div v-if="productionText">
                  <dt>Produção</dt>
                  <dd>{{ productionText }}</dd>
                </div>
                <div v-if="deliveryOperationText">
                  <dt>Operação da entrega</dt>
                  <dd>{{ deliveryOperationText }}</dd>
                </div>
              </dl>

              <div
                v-if="order.tipo_entrega === 'delivery'"
                class="drawer-delivery-workspace"
              >
                <div class="drawer-delivery-heading">
                  <div>
                    <strong>Entregador do pedido</strong>
                    <span>
                      Atribua ou troque o responsável sem sair do Kanban.
                    </span>
                  </div>
                  <span class="drawer-delivery-status">
                    {{ deliveryStatusLabel }}
                  </span>
                </div>

                <div
                  class="drawer-delivery-progress"
                  aria-label="Progresso da entrega"
                >
                  <span
                    v-for="(step, index) in deliverySteps"
                    :key="step.key"
                    :class="{
                      done: index < deliveryStepIndex,
                      active: index === deliveryStepIndex,
                    }"
                  >
                    <i aria-hidden="true"></i>
                    {{ step.label }}
                  </span>
                </div>

                <p v-if="deliveryCourier" class="drawer-delivery-current">
                  <i class="fas fa-motorcycle" aria-hidden="true"></i>
                  Responsável atual: <strong>{{ deliveryCourier.name }}</strong>
                </p>

                <div
                  v-if="deliveryAwaitingReturn"
                  class="drawer-delivery-return"
                  :class="{ 'is-paid': !paymentPending }"
                  role="status"
                >
                  <i
                    :class="
                      paymentPending
                        ? 'fas fa-cash-register'
                        : 'fas fa-check-circle'
                    "
                    aria-hidden="true"
                  ></i>
                  <div>
                    <strong>Entrega realizada — retorno pendente</strong>
                    <span>
                      {{
                        paymentPending
                          ? "Receba no Checkout fiscal para registrar pré-venda ou NFC-e e concluir o retorno."
                          : "O pagamento já está confirmado. Conclua o retorno para finalizar o pedido."
                      }}
                    </span>
                  </div>
                </div>

                <template v-if="canAssignCourier">
                  <label class="drawer-delivery-courier-field">
                    <span>Selecionar entregador</span>
                    <select
                      id="pedido-delivery-courier"
                      v-model="selectedCourierId"
                      class="form-control"
                      :disabled="couriersLoading || actionLoading"
                    >
                      <option value="">
                        {{
                          couriersLoading
                            ? "Carregando entregadores..."
                            : "Selecione um entregador"
                        }}
                      </option>
                      <option
                        v-for="courier in activeCouriers"
                        :key="courier.id"
                        :value="String(courier.id)"
                      >
                        {{ courierName(courier) }}
                      </option>
                    </select>
                  </label>

                  <p
                    v-if="!couriersLoading && !activeCouriers.length"
                    class="drawer-delivery-empty"
                  >
                    Nenhum entregador ativo. Cadastre ou ative um usuário em
                    Cardápio Digital → Atendimento → Entrega.
                  </p>
                </template>
              </div>
            </section>

            <section class="drawer-section drawer-timeline-section">
              <button
                type="button"
                class="drawer-timeline-toggle"
                :aria-expanded="timelineOpen ? 'true' : 'false'"
                @click="toggleTimeline"
              >
                <span>
                  <i class="fas fa-history" aria-hidden="true"></i>
                  Linha do tempo
                </span>
                <i
                  :class="
                    timelineOpen ? 'fas fa-chevron-up' : 'fas fa-chevron-down'
                  "
                  aria-hidden="true"
                ></i>
              </button>

              <div v-if="timelineOpen" class="drawer-timeline">
                <div v-if="timelineLoading" class="drawer-empty" role="status">
                  Carregando linha do tempo...
                </div>
                <div v-else-if="!timeline.length" class="drawer-empty">
                  Nenhuma movimentação registrada.
                </div>
                <article
                  v-for="(event, index) in timeline"
                  v-else
                  :key="timelineKey(event, index)"
                >
                  <span class="timeline-marker" aria-hidden="true"></span>
                  <div>
                    <strong>{{ timelineTitle(event) }}</strong>
                    <p v-if="timelineDescription(event)">
                      {{ timelineDescription(event) }}
                    </p>
                    <time>{{
                      dateTime(event.created_at || event.occurred_at)
                    }}</time>
                  </div>
                </article>
                <button
                  v-if="timelineNextCursor"
                  type="button"
                  class="btn btn-light-primary btn-sm font-weight-bolder"
                  :disabled="timelineLoading"
                  @click="$emit('load-timeline', true)"
                >
                  Carregar mais movimentações
                </button>
              </div>
            </section>
          </template>
        </div>

        <footer v-if="order" class="pedido-drawer-footer">
          <button
            v-if="primaryAction"
            type="button"
            class="btn btn-primary font-weight-bolder drawer-primary-action"
            :disabled="primaryActionDisabled"
            @click="runPrimaryAction"
          >
            <span
              v-if="actionLoading"
              class="spinner-border spinner-border-sm mr-2"
              aria-hidden="true"
            ></span>
            {{ primaryAction.label }}
          </button>
          <span v-else class="drawer-no-action">
            {{
              actionBlockedMessage
                ? actionBlockedMessage.toLowerCase().includes("permiss")
                  ? "Recebimento exige permissão de Caixa"
                  : "Resolva a pendência acima para continuar"
                : "Sem ação operacional pendente"
            }}
          </span>

          <b-dropdown
            right
            dropup
            variant="light-dark"
            toggle-class="font-weight-bolder drawer-more-toggle"
            text="Mais ações"
            :disabled="actionLoading"
          >
            <b-dropdown-item-button
              v-if="can('finalize_delivery')"
              @click="$emit('secondary', 'finalize_delivery')"
            >
              Finalizar pedido pelo painel
            </b-dropdown-item-button>
            <b-dropdown-item-button
              v-if="can('mark_ready')"
              @click="$emit('secondary', 'mark_ready')"
            >
              Marcar como pronto
            </b-dropdown-item-button>
            <b-dropdown-item-button
              v-if="can('open_production')"
              @click="$emit('secondary', 'open_production')"
            >
              Abrir produção
            </b-dropdown-item-button>
            <b-dropdown-item-button
              v-if="can('refuse')"
              @click="$emit('secondary', 'refuse')"
            >
              Recusar pedido
            </b-dropdown-item-button>
            <b-dropdown-item-button
              v-if="can('cancel')"
              @click="$emit('secondary', 'cancel')"
            >
              Cancelar pedido
            </b-dropdown-item-button>
            <b-dropdown-divider
              v-if="can('open_production') || can('refuse') || can('cancel')"
            />
            <b-dropdown-item-button @click="$emit('print', 'checkout')">
              Imprimir pedido
            </b-dropdown-item-button>
            <b-dropdown-item-button
              :disabled="paymentPending"
              @click="$emit('print', 'comprovante')"
            >
              Imprimir comprovante
            </b-dropdown-item-button>
          </b-dropdown>
        </footer>
      </aside>
    </div>
  </transition>
</template>

<script>
import {
  normalizePayment,
  paymentMethodLabel,
} from "../../services/onlineOrder";
import {
  formatDateTime,
  formatDuration,
  formatMoney,
  orderCode,
  orderCustomer,
  orderId,
  PAYMENT_STATUS_LABELS,
  stageAgeSeconds,
  STATUS_LABELS,
} from "./orderBoard";

export default {
  name: "PedidoOnlineDrawer",
  props: {
    visible: { type: Boolean, default: false },
    order: { type: Object, default: null },
    loading: { type: Boolean, default: false },
    actionLoading: { type: Boolean, default: false },
    primaryAction: { type: Object, default: null },
    availableActions: { type: Array, default: () => [] },
    actionBlockedMessage: { type: String, default: "" },
    timeline: { type: Array, default: () => [] },
    timelineLoading: { type: Boolean, default: false },
    timelineNextCursor: { type: [String, Number], default: null },
    couriers: { type: Array, default: () => [] },
    couriersLoading: { type: Boolean, default: false },
    serverTime: { type: String, default: null },
    now: { type: Number, default: () => Date.now() },
  },
  data() {
    return {
      timelineOpen: false,
      timelineRequested: false,
      previousBodyOverflow: "",
      selectedCourierId: "",
    };
  },
  computed: {
    titleId() {
      return `pedido-drawer-title-${
        this.order ? orderId(this.order) : "loading"
      }`;
    },
    code() {
      return this.order ? orderCode(this.order) : "Carregando";
    },
    customer() {
      return orderCustomer(this.order);
    },
    statusLabel() {
      return STATUS_LABELS[this.order?.status] || this.order?.status || "-";
    },
    statusClass() {
      return ["drawer-status", `status-${this.order?.status || "default"}`];
    },
    ageLabel() {
      return formatDuration(
        stageAgeSeconds(this.order, this.serverTime, this.now)
      );
    },
    deliveryLabel() {
      return this.order?.tipo_entrega === "delivery" ? "Entrega" : "Retirada";
    },
    items() {
      return this.order?.itens || this.order?.items || [];
    },
    itemCountLabel() {
      const count = this.items.reduce(
        (total, item) => total + Number(item.quantidade || 1),
        0
      );
      return `${count} ${count === 1 ? "item" : "itens"}`;
    },
    payment() {
      const value =
        this.order?.payment ||
        this.order?.pagamento ||
        (Array.isArray(this.order?.pagamentos)
          ? this.order.pagamentos[0]
          : null);
      return normalizePayment(value || {});
    },
    paymentMethod() {
      return paymentMethodLabel(this.payment.metodo, this.order?.tipo_entrega);
    },
    paymentStatusLabel() {
      if (
        ["pago", "aprovado"].includes(this.payment.status) &&
        !this.paymentOperationallyConfirmed
      ) {
        return "Recebimento anterior — revisar no checkout";
      }
      return (
        PAYMENT_STATUS_LABELS[this.payment.status] || this.payment.status || "-"
      );
    },
    paymentPending() {
      return !this.paymentOperationallyConfirmed;
    },
    paymentOperationallyConfirmed() {
      const context = this.order?.checkout_context || {};
      const operation =
        this.order?.operacao || this.order?.operational_link || {};
      return Boolean(
        this.order?.operational_payment_confirmed === true ||
          context.checkout_concluido === true ||
          this.payment.operationally_confirmed === true ||
          (operation.liquidado_at &&
            (operation.fechamento_id ||
              operation.nfe_id ||
              operation.pre_venda_id))
      );
    },
    phone() {
      return (
        this.order?.cliente_telefone || this.order?.cliente?.telefone || null
      );
    },
    phoneHref() {
      return `tel:${String(this.phone || "").replace(/[^+\d]/g, "")}`;
    },
    address() {
      const address = this.order?.endereco;
      if (!address) return "Endereço não informado";
      const street = [
        address.logradouro,
        address.numero ? `nº ${address.numero}` : null,
        address.complemento,
      ]
        .filter(Boolean)
        .join(", ");
      const city = [address.bairro, address.cidade, address.uf]
        .filter(Boolean)
        .join(" · ");
      const reference = address.referencia
        ? `Referência: ${address.referencia}`
        : null;
      return [street, city, reference].filter(Boolean).join(" — ");
    },
    productionText() {
      const production = this.order?.production || this.order?.producao || {};
      const done = Number(
        production.ready ?? production.completed ?? production.concluidas ?? 0
      );
      const total = Number(production.total ?? production.filas ?? 0);
      if (total) return `${done} de ${total} filas concluídas`;
      return production.status_label || production.situacao_label || null;
    },
    deliveryOperationText() {
      const delivery = this.delivery;
      return (
        delivery.status_label ||
        delivery.situacao_label ||
        delivery.courier?.name ||
        delivery.entregador_nome ||
        null
      );
    },
    delivery() {
      return (
        this.order?.delivery ||
        this.order?.entrega ||
        this.order?.entrega_operacional ||
        {}
      );
    },
    deliveryStatus() {
      return String(
        this.delivery.status ||
          this.delivery.situacao ||
          (this.order?.status === "saiu_para_entrega"
            ? "saiu_entrega"
            : this.order?.status === "entregue"
            ? "entregue"
            : "aguardando_atribuicao")
      ).toLowerCase();
    },
    deliveryStatusLabel() {
      const labels = {
        aguardando_atribuicao: "Aguardando entregador",
        atribuida: "Entrega atribuída",
        aceita: "Aceita pelo entregador",
        saiu_entrega: "Em rota",
        em_rota: "Em rota",
        entregue: "Entregue ao cliente",
        retornou: "Retorno concluído",
      };
      return (
        this.delivery.status_label ||
        this.delivery.situacao_label ||
        labels[this.deliveryStatus] ||
        "Preparando entrega"
      );
    },
    deliveryCourier() {
      const courier = this.delivery.courier || this.delivery.entregador;
      if (!courier && !this.delivery.entregador_nome) return null;
      return {
        id: courier?.id || this.delivery.entregador_id || null,
        name:
          courier?.name ||
          courier?.nome_exibicao ||
          courier?.nome ||
          this.delivery.entregador_nome ||
          "Entregador",
      };
    },
    activeCouriers() {
      return this.couriers.filter((courier) => courier.ativo !== false);
    },
    canAssignCourier() {
      return this.availableActions.includes("assign_courier");
    },
    assigningCourier() {
      return this.primaryAction?.key === "assign_courier";
    },
    deliveryAwaitingReturn() {
      return (
        this.order?.status === "entregue" && this.deliveryStatus === "entregue"
      );
    },
    primaryActionDisabled() {
      return Boolean(
        this.actionLoading ||
          (this.assigningCourier &&
            (!this.selectedCourierId || !this.activeCouriers.length))
      );
    },
    deliverySteps() {
      return [
        { key: "courier", label: "Entregador" },
        { key: "route", label: "Em rota" },
        { key: "delivered", label: "Entregue" },
        { key: "return", label: "Retorno" },
      ];
    },
    deliveryStepIndex() {
      const indexes = {
        aguardando_atribuicao: 0,
        atribuida: 0,
        aceita: 0,
        saiu_entrega: 1,
        em_rota: 1,
        entregue: 2,
        retornou: 3,
      };
      return indexes[this.deliveryStatus] ?? 0;
    },
  },
  watch: {
    visible(value) {
      if (value) this.openDrawer();
      else this.restoreBody();
    },
    "order.id"() {
      this.timelineOpen = false;
      this.timelineRequested = false;
      this.syncSelectedCourier();
    },
    deliveryCourier: {
      immediate: true,
      handler() {
        this.syncSelectedCourier();
      },
    },
  },
  mounted() {
    if (this.visible) this.openDrawer();
  },
  beforeDestroy() {
    this.restoreBody();
    document.removeEventListener("keydown", this.handleKeydown);
  },
  methods: {
    money: formatMoney,
    dateTime: formatDateTime,
    can(action) {
      return this.availableActions.includes(action);
    },
    courierName(courier) {
      return (
        courier.nome ||
        courier.nome_exibicao ||
        courier.name ||
        courier.usuario?.nome ||
        courier.usuario?.usuario ||
        "Entregador"
      );
    },
    syncSelectedCourier() {
      this.selectedCourierId = this.deliveryCourier?.id
        ? String(this.deliveryCourier.id)
        : "";
    },
    runPrimaryAction() {
      this.$emit(
        "primary",
        this.assigningCourier ? this.selectedCourierId : undefined
      );
    },
    openDrawer() {
      this.previousBodyOverflow = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      document.addEventListener("keydown", this.handleKeydown);
      this.$nextTick(() => this.$refs.closeButton?.focus());
    },
    restoreBody() {
      document.body.style.overflow = this.previousBodyOverflow;
      document.removeEventListener("keydown", this.handleKeydown);
    },
    handleKeydown(event) {
      if (!this.visible) return;
      if (event.key === "Escape") {
        event.preventDefault();
        this.$emit("close");
        return;
      }
      if (event.key !== "Tab" || !this.$refs.drawer) return;
      const focusable = Array.from(
        this.$refs.drawer.querySelectorAll(
          'button:not([disabled]), a[href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
        )
      );
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      const focusIsInside = this.$refs.drawer.contains(document.activeElement);
      if (!focusIsInside) {
        event.preventDefault();
        (event.shiftKey ? last : first).focus();
      } else if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    },
    toggleTimeline() {
      this.timelineOpen = !this.timelineOpen;
      if (this.timelineOpen && !this.timelineRequested) {
        this.timelineRequested = true;
        this.$emit("load-timeline", false);
      }
    },
    itemKey(item, index) {
      return (
        item.id || item.codigo_produto || `${item.nome || "item"}-${index}`
      );
    },
    itemName(item) {
      return (
        item.nome_produto ||
        item.nome ||
        item.produto?.nome ||
        item.produto ||
        "Item"
      );
    },
    itemTotalValue(item) {
      if (item.valor_total !== null && item.valor_total !== undefined) {
        return item.valor_total;
      }
      if (item.total !== null && item.total !== undefined) return item.total;
      return item.valor;
    },
    itemSnapshot(item) {
      const snapshot = item.configuracao_snapshot || item.configuracao || {};
      if (typeof snapshot !== "string") return snapshot || {};
      try {
        return JSON.parse(snapshot);
      } catch (_) {
        return {};
      }
    },
    itemDetails(item) {
      const details = [];
      const snapshot = this.itemSnapshot(item);
      const pizza = item.pizza || snapshot.pizza;
      if (pizza) {
        const size = pizza.tamanho?.nome || pizza.tamanho_nome || pizza.tamanho;
        if (size) details.push(`Tamanho: ${size}`);
        const flavors = pizza.sabores || [];
        if (flavors.length) {
          details.push(
            flavors
              .map((flavor) => {
                const name =
                  flavor.nome || flavor.sabor_nome || flavor.descricao;
                const fraction = flavor.fracao_label || flavor.fracao || null;
                return [fraction, name].filter(Boolean).join(" ");
              })
              .filter(Boolean)
              .join(" + ")
          );
        }
        const border = pizza.borda?.nome || pizza.borda_nome || pizza.borda;
        if (border && typeof border === "string")
          details.push(`Borda: ${border}`);
      }

      const modifiers = [
        ...(item.adicionais || []),
        ...(item.modificadores || []),
        ...(snapshot.modificadores || []),
      ];
      modifiers.forEach((modifier) => {
        const name = modifier.nome_opcao || modifier.nome || modifier.descricao;
        if (name) {
          const quantity = Number(modifier.quantidade || 1);
          details.push(`${quantity > 1 ? `${quantity}x ` : ""}${name}`);
        }
      });
      if (item.observacao || snapshot.observacao) {
        details.push(`Observação: ${item.observacao || snapshot.observacao}`);
      }
      return Array.from(new Set(details));
    },
    itemChildren(item) {
      return (
        item.itens_filhos || item.children || item.produtos_adicionais || []
      );
    },
    timelineKey(event, index) {
      return (
        event.id ||
        `${event.created_at || event.occurred_at || "event"}-${index}`
      );
    },
    timelineTitle(event) {
      return (
        event.title ||
        event.titulo ||
        STATUS_LABELS[event.status_para || event.status] ||
        event.tipo ||
        "Movimentação"
      );
    },
    timelineDescription(event) {
      return (
        event.description ||
        event.descricao ||
        event.observacao ||
        event.origem ||
        null
      );
    },
  },
};
</script>

<style scoped>
.pedido-drawer-layer {
  background: rgba(24, 28, 50, 0.45);
  inset: 0;
  position: fixed;
  z-index: 1100;
}

.pedido-drawer {
  background: #fff;
  box-shadow: -12px 0 35px rgba(24, 28, 50, 0.2);
  display: flex;
  flex-direction: column;
  height: 100vh;
  margin-left: auto;
  max-width: 100%;
  outline: none;
  width: 520px;
}

@supports (height: 100dvh) {
  .pedido-drawer {
    height: 100dvh;
  }
}

.pedido-drawer-header,
.pedido-drawer-footer {
  background: #fff;
  flex: 0 0 auto;
  padding: 18px 20px;
  position: relative;
  z-index: 2;
}

.pedido-drawer-header {
  align-items: flex-start;
  border-bottom: 1px solid #e4e6ef;
  display: flex;
  justify-content: space-between;
}

.pedido-drawer-title > span {
  color: #7e8299;
  font-size: 11px;
  font-weight: 800;
  text-transform: uppercase;
}

.pedido-drawer-title h2 {
  color: #181c32;
  font-size: 21px;
  font-weight: 800;
  margin: 2px 0 7px;
}

.pedido-drawer-statusline {
  align-items: center;
  color: #7e8299;
  display: flex;
  flex-wrap: wrap;
  font-size: 11px;
  font-weight: 700;
  gap: 8px;
}

.drawer-status {
  background: #eef6ff;
  border-radius: 999px;
  color: #2875bf;
  padding: 4px 8px;
}

.status-pronto,
.status-entregue,
.status-concluido {
  background: #e8fff3;
  color: #0b8d68;
}

.status-cancelado,
.status-recusado,
.status-pagamento_expirado {
  background: #fff1f2;
  color: #c93649;
}

.pedido-drawer-close {
  align-items: center;
  background: #f3f6f9;
  border: 0;
  border-radius: 8px;
  color: #5e6278;
  display: inline-flex;
  height: 44px;
  justify-content: center;
  width: 44px;
}

.pedido-drawer-close:focus-visible,
.drawer-timeline-toggle:focus-visible {
  outline: 2px solid #3699ff;
  outline-offset: 2px;
}

.pedido-drawer-body {
  flex: 1 1 auto;
  overflow-y: auto;
  overscroll-behavior: contain;
  padding: 18px 20px 30px;
}

.drawer-loading {
  align-items: center;
  color: #7e8299;
  display: flex;
  flex-direction: column;
  font-weight: 700;
  gap: 12px;
  justify-content: center;
  min-height: 280px;
}

.drawer-highlight-grid {
  display: grid;
  gap: 9px;
  grid-template-columns: repeat(2, minmax(0, 1fr));
}

.drawer-highlight-grid > div {
  background: #f7f8fb;
  border: 1px solid #ebedf3;
  border-radius: 8px;
  min-width: 0;
  padding: 11px 12px;
}

.drawer-highlight-grid span,
.drawer-definition-list dt {
  color: #7e8299;
  display: block;
  font-size: 10px;
  font-weight: 800;
  text-transform: uppercase;
}

.drawer-highlight-grid strong {
  color: #181c32;
  display: block;
  margin-top: 3px;
  overflow-wrap: anywhere;
}

.drawer-section {
  border-top: 1px solid #ebedf3;
  margin-top: 20px;
  padding-top: 20px;
}

.drawer-section h3,
.drawer-section-title h3 {
  color: #181c32;
  font-size: 15px;
  font-weight: 800;
  margin: 0 0 12px;
}

.drawer-section-title {
  align-items: center;
  display: flex;
  justify-content: space-between;
}

.drawer-section-title > span {
  color: #7e8299;
  font-size: 11px;
  font-weight: 700;
}

.drawer-order-item {
  border: 1px solid #e4e6ef;
  border-radius: 9px;
  margin-top: 9px;
  padding: 12px;
}

.drawer-item-main {
  align-items: flex-start;
  display: grid;
  gap: 9px;
  grid-template-columns: auto 1fr auto;
}

.drawer-item-main > div {
  min-width: 0;
}

.drawer-item-quantity {
  background: #eef6ff;
  border-radius: 6px;
  color: #2875bf;
  font-size: 11px;
  font-weight: 800;
  padding: 4px 6px;
}

.drawer-item-main strong {
  color: #181c32;
  overflow-wrap: anywhere;
}

.drawer-item-main ul {
  color: #5e6278;
  font-size: 12px;
  list-style: none;
  margin: 6px 0 0;
  padding: 0;
}

.drawer-item-main li + li {
  margin-top: 3px;
}

.drawer-item-total {
  white-space: nowrap;
}

.drawer-child-item {
  align-items: center;
  background: #f7f8fb;
  border-radius: 7px;
  color: #5e6278;
  display: grid;
  font-size: 12px;
  gap: 8px;
  grid-template-columns: auto 1fr auto;
  margin: 9px 0 0 28px;
  padding: 8px 9px;
}

.drawer-child-item i {
  transform: rotate(90deg);
}

.drawer-child-item small {
  color: #7e8299;
  display: block;
}

.drawer-child-item span {
  min-width: 0;
  overflow-wrap: anywhere;
}

.drawer-definition-list {
  margin: 0;
}

.drawer-definition-list > div {
  border-bottom: 1px solid #f0f1f5;
  display: grid;
  gap: 14px;
  grid-template-columns: 120px minmax(0, 1fr);
  padding: 9px 0;
}

.drawer-definition-list dd {
  color: #3f4254;
  font-weight: 600;
  margin: 0;
  overflow-wrap: anywhere;
}

.drawer-definition-list a {
  align-items: center;
  display: inline-flex;
  min-height: 44px;
}

.drawer-delivery-workspace {
  background: #f7f8fb;
  border: 1px solid #e4e6ef;
  border-radius: 10px;
  margin-top: 14px;
  padding: 12px;
}

.drawer-delivery-heading {
  align-items: flex-start;
  display: flex;
  gap: 10px;
  justify-content: space-between;
}

.drawer-delivery-heading strong,
.drawer-delivery-heading span {
  display: block;
}

.drawer-delivery-heading > div > span {
  color: #7e8299;
  font-size: 11px;
  margin-top: 2px;
}

.drawer-delivery-status {
  background: #eef6ff;
  border-radius: 999px;
  color: #2875bf;
  flex: 0 0 auto;
  font-size: 10px;
  font-weight: 800;
  padding: 5px 8px;
}

.drawer-delivery-progress {
  display: grid;
  gap: 5px;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  margin-top: 14px;
}

.drawer-delivery-progress span {
  color: #a2a5b9;
  font-size: 9px;
  font-weight: 800;
  min-width: 0;
  text-align: center;
}

.drawer-delivery-progress i {
  background: #d9dce6;
  border-radius: 999px;
  display: block;
  height: 4px;
  margin-bottom: 5px;
  width: 100%;
}

.drawer-delivery-progress .done,
.drawer-delivery-progress .active {
  color: #2875bf;
}

.drawer-delivery-progress .done i,
.drawer-delivery-progress .active i {
  background: #3699ff;
}

.drawer-delivery-current {
  color: #5e6278;
  font-size: 11px;
  margin: 10px 0 0;
}

.drawer-delivery-current i {
  color: #3699ff;
  margin-right: 5px;
}

.drawer-delivery-return {
  align-items: flex-start;
  background: #fff7e6;
  border: 1px solid #ffd591;
  border-radius: 8px;
  color: #895400;
  display: flex;
  gap: 9px;
  margin-top: 12px;
  padding: 10px;
}

.drawer-delivery-return.is-paid {
  background: #e8fff3;
  border-color: #b8edd3;
  color: #147554;
}

.drawer-delivery-return > i {
  margin-top: 2px;
}

.drawer-delivery-return strong,
.drawer-delivery-return span {
  display: block;
}

.drawer-delivery-return span {
  font-size: 11px;
  line-height: 1.4;
  margin-top: 2px;
}

.drawer-delivery-courier-field {
  display: grid;
  gap: 6px;
  margin: 13px 0 0;
}

.drawer-delivery-courier-field > span {
  color: #5e6278;
  font-size: 11px;
  font-weight: 800;
}

.drawer-delivery-courier-field select {
  min-height: 44px;
}

.drawer-delivery-empty {
  background: #fff7e6;
  border: 1px solid #ffd591;
  border-radius: 7px;
  color: #7e5b27;
  font-size: 11px;
  line-height: 1.45;
  margin: 10px 0 0;
  padding: 9px;
}

.drawer-timeline-toggle {
  align-items: center;
  background: transparent;
  border: 0;
  color: #181c32;
  display: flex;
  font-weight: 800;
  justify-content: space-between;
  min-height: 44px;
  padding: 0;
  width: 100%;
}

.drawer-timeline-toggle span {
  align-items: center;
  display: flex;
  gap: 8px;
}

.drawer-timeline article {
  display: grid;
  gap: 10px;
  grid-template-columns: 10px 1fr;
  padding: 8px 0;
}

.timeline-marker {
  background: #3699ff;
  border: 3px solid #dceeff;
  border-radius: 50%;
  height: 10px;
  margin-top: 4px;
  width: 10px;
}

.drawer-timeline p,
.drawer-timeline time {
  color: #7e8299;
  display: block;
  font-size: 11px;
  margin: 2px 0 0;
}

.drawer-timeline .btn {
  min-height: 44px;
}

.drawer-empty {
  color: #7e8299;
  font-size: 12px;
  padding: 14px 0;
  text-align: center;
}

.pedido-drawer-footer {
  align-items: center;
  border-top: 1px solid #e4e6ef;
  box-shadow: 0 -5px 18px rgba(24, 28, 50, 0.05);
  display: flex;
  gap: 10px;
  justify-content: space-between;
}

.drawer-primary-action {
  flex: 1 1 auto;
  min-height: 48px;
}

.drawer-no-action {
  color: #7e8299;
  font-size: 12px;
  font-weight: 700;
}

.pedido-drawer-footer::v-deep .drawer-more-toggle,
.pedido-drawer-footer::v-deep .dropdown-item {
  min-height: 44px;
}

.pedido-drawer-fade-enter-active,
.pedido-drawer-fade-leave-active {
  transition: opacity 0.18s ease;
}

.pedido-drawer-fade-enter-active .pedido-drawer,
.pedido-drawer-fade-leave-active .pedido-drawer {
  transition: transform 0.18s ease;
}

.pedido-drawer-fade-enter,
.pedido-drawer-fade-leave-to {
  opacity: 0;
}

.pedido-drawer-fade-enter .pedido-drawer,
.pedido-drawer-fade-leave-to .pedido-drawer {
  transform: translateX(100%);
}

@media (max-width: 767px) {
  .pedido-drawer {
    width: 100%;
  }

  .pedido-drawer-header,
  .pedido-drawer-footer {
    padding: 14px 16px;
  }

  .pedido-drawer-footer {
    padding-bottom: calc(14px + env(safe-area-inset-bottom));
  }

  .pedido-drawer-body {
    padding: 14px 16px 24px;
  }

  .pedido-drawer-footer {
    align-items: stretch;
    flex-direction: column;
  }

  .pedido-drawer-footer::v-deep .dropdown,
  .pedido-drawer-footer::v-deep .dropdown-toggle {
    width: 100%;
  }
}

@media (max-width: 420px) {
  .drawer-highlight-grid {
    grid-template-columns: 1fr;
  }

  .drawer-definition-list > div {
    grid-template-columns: 1fr;
    gap: 3px;
  }

  .drawer-delivery-heading {
    display: grid;
    grid-template-columns: 1fr;
  }

  .drawer-delivery-status {
    justify-self: start;
  }
}
</style>
